/*! Copyright (c) 2022, XAPP AI */
import { ChatWidgetAppChannelInput, CtaAnimation } from "generated/graphql";
import { getApiUrl, getRouterUrl } from "./getApiUrl";
import { invertColor } from "./color";

export interface ChatWidgetInput {
    name: string;
    website: string;
    logo: string;
    primaryColor: string;
    secondaryColor?: string;
    appId: string;
}

/**
 * Generates the input for the createAppWidget mutation
 *
 * @param appInput
 * @returns
 */
export function generateCreateAppWidgetInput(appInput: ChatWidgetInput): ChatWidgetAppChannelInput {

    const { website, name, logo, appId } = appInput;

    if (!website) {
        throw new Error(`Website was not provided`);
    }

    let url: URL;

    try {
        url = new URL(website);
    } catch (e) {
        throw new Error(`Website ${website} is invalid.`);
    }

    let patternedWebsite = `${url.hostname}${url.pathname}`;

    // Hosts often treat www. and hostnames without subdomains
    // as exactly the same website so we remove the www. to also
    // match same site
    // If domain is www then we remove it
    if (patternedWebsite.toLowerCase().startsWith("www.")) {
        patternedWebsite = patternedWebsite.replace(/www\./i, "");
        // https://[(www.)?]bonedry.com/cincinnati[.+]
    }

    const serverUrl = `${getApiUrl()}/apps/${appId}/run/`;

    const routerUrl = `${getRouterUrl()}/?appId=${appId}&channelId=`;

    let primaryColor = appInput.primaryColor;
    let buttonColor: string;

    // if no primary color, use the default
    if (!primaryColor) {
        primaryColor = "#F9F9F9";
        buttonColor = "#333333";
    } else {
        buttonColor = invertColor(primaryColor, true);
    }

    const widgetInput: ChatWidgetAppChannelInput = {
        type: "chat-widget",
        cta: {
            message: "Need Help? Click Here",
            timeout: 30000,
            delay: 2000,
            animation: "bounce" as CtaAnimation,
            animationTimeout: 5000,
            mobile: {
                applyAtLessThanWidth: "480",
                delay: 0,
                animation: "wiggle" as CtaAnimation,
                animationTimeout: 5000
            }
        },
        serverUrl,
        connection: {
            type: "websocketraw",
            // this will also be updated when we get the chat widget Id
            serverUrl: routerUrl
        },
        // will be updated once we get the key
        // autocompleteSuggestionsUrl: "",
        direct: false,

        botName: name,

        header: {
            status: {
                online: name
            },
            actions: {
                minimize: true,
                cancel: true
            }
        },
        footer: {
            branding: {
                enabled: true,
                text: "Powered by [XAPP AI](https://xapp.ai)",
            }
        },
        urls: {
            policies: [
                {
                    pattern: patternedWebsite,
                    behavior: {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore Trust us
                        type: "sameWindow",
                    }
                }
            ],
            defaultBehavior: {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore Trust us
                type: "newTab"
            }
        },

        theme: {
            zIndex: "10000",
            messages: {
                mine: {
                    bubbleColor: "#f2f2f2",
                    text: {
                        color: "#454545",
                        fontSize: "18px",
                    },
                },
                others: {
                    text: {
                        fontSize: "18px",
                    },
                },
                maxWidth: "400px",
            },
            primaryColor,

            minimizeButton: {
                color: buttonColor
            },
            cancelButton: {
                color: buttonColor
            },
            refreshButton: {
                color: buttonColor
            },

            size: {
                width: "500px",
                height: "700px",
            },
        },
        configurableMessages: {
            items: [
                {
                    delay: 12,
                    text: "Thinking... will have your answer shortly... ",
                },
                {
                    delay: 5,
                    text: "Still thinking... thanks for your patience.",
                },
                {
                    delay: 0,
                    text: "Sorry, I couldn't get the answer for you.  This might be a connection error, in that case please try again later.",
                }
            ]
        }
    }

    if (logo) {
        widgetInput.avatarUrl = logo;
    }

    return widgetInput;
}